import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomSnackbar from "../../components/CustomSnackbar";
import PuzRow from "../../pzl_common/PuzRow";
import VerifyDialog from "../../pzl_common/VerifyDialog";
import WordMeaning from "../../pzl_common/WordMeaning";
import WordRedefine from "../../pzl_common/WordRedefine";
import AllPuzRow from "../../pzl_common/board/AllPuzRow";
import StepFooter from "../../pzl_common/board/StepFooter";
import PuzzleWorkflowStepper from "../../pzl_common/board/PuzzleWorkflowStepper";
import BorderHeader from "../../pzl_common/BorderHeader";
import BorderLeftPanel from "../../pzl_common/BorderLeftPanel";
import { findScatteredWordPositions, patternSearch } from "../../pzl_common/patternSearch";
import WordList from "../../pzl_grid/board/candidates/WordList";
import WordValidation from "../../setting/banned_words/WordValidation";
import AdminCrossBoardService, { DISCARDED, DRAFT, READY_TO_REVIEW } from "./AdminCrossBoardService";
import Logger from "../../../services/Logger";
import { usePuzzleData } from "./usePuzzleData";

export default function Step1Draft() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [refresh, setRefresh] = useState(0);
    const { crossTpl, loading, error: fetchError } = usePuzzleData(id, refresh);
    const [error, setError] = useState(null);
    const [square, setSquare] = useState([]);
    const [searchWord, setSearchWord] = useState("");
    const [positions, setPositions] = useState([]);
    const [showAllWords, setShowAllWords] = useState(false);
    const [allWordPositions, setAllWordPositions] = useState([]);
    const [selectedWordData, setSelectedWordData] = useState(null);
    const [selectedWord, setSelectedWord] = useState(null);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    // Dialog state for editing definition
    const [openDialog, setOpenDialog] = useState(false);
    const [editingDefinition, setEditingDefinition] = useState("");
    const [editingWord, setEditingWord] = useState("");

    // Confirmation dialog state for discarding template
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

    // Confirmation dialog state for deleting word
    const [openDeleteWordDialog, setOpenDeleteWordDialog] = useState(false);
    const [wordToDelete, setWordToDelete] = useState(null);

    // Puzzle type selection dialog state
    const [puzzleTypeDialogOpen, setPuzzleTypeDialogOpen] = useState(false);

    // Left panel (word search / word list / word meaning) fold state
    const [leftPanelFolded, setLeftPanelFolded] = useState(false);

    // Memoized parsed filledCoordinates to avoid duplicate parsing
    const filledCoordinates = useMemo(() => {
        return crossTpl?.filledCoordinates ? JSON.parse(crossTpl.filledCoordinates) : [];
    }, [crossTpl?.filledCoordinates]);

    // Puzzle completion stats for threshold message
    const puzzleStats = useMemo(() => {
        const totalCells = 12 * 12;
        const cellSet = new Set();
        (filledCoordinates || []).forEach((coord) => {
            (coord.positions || []).forEach((p) => {
                if (p.row != null && p.col != null) cellSet.add(`${p.row},${p.col}`);
            });
        });
        const filledCells = cellSet.size;
        const percentage = totalCells > 0 ? (filledCells / totalCells) * 100 : 0;
        const thresholdPercentage = parseInt(process.env.REACT_APP_CROSSWORD_THRESHOLD_PERCENTAGE || "85", 10);
        const thresholdCells = Math.ceil((thresholdPercentage / 100) * totalCells);
        const thresholdMet = percentage >= thresholdPercentage;
        const cellsNeeded = thresholdMet ? 0 : Math.max(0, thresholdCells - filledCells);
        return { filledCells, totalCells, percentage, thresholdPercentage, thresholdMet, cellsNeeded };
    }, [filledCoordinates]);

    // Function to find character at position
    const findCharacter = useCallback((row, col, filledCoordinates) => {
        let found = "";

        if (filledCoordinates) {
            found = filledCoordinates
                .map((c) => c.positions)
                .map((positions) => {
                    const matchingPosition = positions.find((p) => p.row === row && p.col === col);

                    if (matchingPosition) {
                        return matchingPosition.value ? matchingPosition.value.toUpperCase() : "";
                    }
                    return "";
                })
                .find((value) => value !== "");
        }

        return found || "";
    }, []);

    // Function to initialize the square array
    const initializeSquare = useCallback((filledCoordinates) => {
        const size = 12;
        let newSquare = Array.from({ length: size }, () => Array(size).fill(0));

        for (let row = 0; row < size; row++) {
            for (let col = 0; col < size; col++) {
                newSquare[row][col] = findCharacter(row, col, filledCoordinates);
            }
        }

        return newSquare;
    }, [findCharacter]);

    // Handle snackbar function
    const handleSnackbar = useCallback((message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setOpenSnackbar(true);
    }, []);

    useEffect(() => {
        // Logger.log("crossTpl", crossTpl);
        if (filledCoordinates.length > 0) {
            const newSquare = initializeSquare(filledCoordinates);
            setSquare(newSquare);
        } else {
            // Even if filledCoordinates is empty, initialize an empty 12x12 grid
            const newSquare = initializeSquare([]);
            setSquare(newSquare);
        }
    }, [filledCoordinates, initializeSquare]);

    // Check if puzzle is discarded and redirect
    useEffect(() => {
        if (crossTpl && crossTpl.state === DISCARDED) {
            handleSnackbar("This puzzle has been discarded", "warning");
            navigate("/admin/puzzle/cross_board");
        }
    }, [crossTpl, navigate, handleSnackbar]);

    // Calculate all word positions when square and wordList change
    useEffect(() => {
        if (square.length > 0 && crossTpl?.wordsInUse) {
            const wordList = crossTpl.wordsInUse.split('\n').filter(word => word.trim());
            const allPositions = wordList.map(word => {
                const wordText = word.trim();
                // First try to find continuous patterns
                let positions = patternSearch(square, wordText.toUpperCase());

                // If no continuous pattern found, try to find scattered positions
                if (positions.length === 0) {
                    positions = findScatteredWordPositions(filledCoordinates, wordText.toUpperCase());
                }

                return {
                    word: wordText,
                    positions: positions
                };
            }).filter(item => item.positions.length > 0);
            setAllWordPositions(allPositions);
        }
    }, [square, crossTpl?.wordsInUse, filledCoordinates]);

    const handleNextStep = async () => {
        // Open puzzle type selection dialog instead of directly updating state
        setPuzzleTypeDialogOpen(true);
    };

    const handlePuzzleTypeSelected = async (puzzleType) => {
        try {
            setError(null);
            // Call API to change state to READY_TO_REVIEW with puzzle type
            await AdminCrossBoardService.updateCrossTplState(crossTpl.id, READY_TO_REVIEW, puzzleType);
            // Trigger refresh to get updated puzzle data
            setRefresh(prev => prev + 1);
            // Navigate to ready-for-review page
            navigate(`/admin/puzzle/cross_board/ready-to-review/${crossTpl.id}`);
        } catch (err) {
            Logger.error("Error updating puzzle state:", err);
            setError("Failed to move to next step. Please try again.");
        }
    };

    const handleDiscardTemplate = () => {
        // Show confirmation dialog instead of immediately discarding
        setOpenConfirmDialog(true);
    };

    const handleConfirmDiscard = async (targetId) => {
        try {
            setError(null);
            // Call API to mark template as discarded
            await AdminCrossBoardService.markAsDiscarded(targetId);
            // Show success message
            handleSnackbar("Template discarded successfully", "success");
            // Navigate back to puzzle list
            navigate("/admin/puzzle/cross_board");
        } catch (err) {
            Logger.error("Error discarding template:", err);
            setError("Failed to discard template. Please try again.");
        } finally {
            setOpenConfirmDialog(false);
        }
    };

    const handleDeleteWord = () => {
        if (selectedWord) {
            setWordToDelete(selectedWord);
            setOpenDeleteWordDialog(true);
        }
    };

    const handleConfirmDeleteWord = async () => {
        // Set cursor to wait
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';
        
        try {
            setError(null);

            // Get current filled coordinates and remove the word's positions
            const updatedCoords = filledCoordinates.filter(coord =>
                coord.text && coord.text.toUpperCase() !== wordToDelete?.toUpperCase()
            );

            // Call API to update the puzzle
            await AdminCrossBoardService.updatePuzzleCoordinates(crossTpl.id, updatedCoords, null);

            // Show success message
            handleSnackbar(`Word "${wordToDelete}" removed successfully`, "success");

            // Clear selected word and refresh puzzle data
            setSelectedWord(null);
            setSearchWord("");
            setPositions([]);
            setSelectedWordData(null);
            setRefresh(prev => prev + 1);

        } catch (err) {
            Logger.error("Error deleting word:", err);
            setError("Failed to delete word. Please try again.");
        } finally {
            // Restore cursor to normal
            document.body.style.cursor = originalCursor || 'default';
            setOpenDeleteWordDialog(false);
            setWordToDelete(null);
        }
    };

    const handleBack = () => {
        const fromStep = searchParams.get('fromStep');
        if (fromStep !== null) {
            navigate(`/admin/puzzle/cross_board?activeStep=${fromStep}`);
        } else {
            navigate("/admin/puzzle/cross_board");
        }
    };

    const handleWordSearch = (e) => {
        // Don't allow search if puzzle is discarded
        if (crossTpl?.state === DISCARDED) {
            return;
        }

        const str = e.target.value.toUpperCase();
        setSearchWord(str);

        if (str && square.length > 0) {
            // First try to find continuous patterns
            let foundPositions = patternSearch(square, str);

            // If no continuous pattern found, try to find scattered positions
            if (foundPositions.length === 0) {
                foundPositions = findScatteredWordPositions(filledCoordinates, str);
            }

            setPositions(foundPositions);
        } else {
            setPositions([]);
        }
    };

    const handleSaveTitle = (definition, word) => {
        // Don't allow saving if puzzle is discarded
        if (crossTpl?.state === DISCARDED) {
            return;
        }

        // Open dialog to edit definition before saving
        setEditingDefinition(definition);
        setEditingWord(word);
        setOpenDialog(true);
    };

    const handleDialogSave = (editedDefinition, saveAsHiddenMessage = false) => {
        // Save the edited definition
        AdminCrossBoardService.setClue(editingWord, editedDefinition, crossTpl.id, saveAsHiddenMessage).then(() => {
            handleSnackbar("Definition saved successfully", "success");
            setOpenDialog(false);
            setEditingDefinition("");
            setEditingWord("");
            setRefresh(Date.now());
        }).catch(() => {
            handleSnackbar("Error saving definition", "error");
        });
    };

    const handleDialogCancel = () => {
        setOpenDialog(false);
        setEditingDefinition("");
        setEditingWord("");
    };

    const handleFindWordCandidates = () => {
        if (square.length === 0) {
            handleSnackbar("Puzzle grid is not ready yet. Please wait a moment and try again.", "warning");
            return;
        }

        // Check if puzzle is discarded
        if (crossTpl.state === DISCARDED) {
            handleSnackbar("This puzzle has been discarded and is no longer available.", "warning");
            return;
        }

        // Navigate to the word candidates edit page
        navigate(`/admin/puzzle/cross_board/draft/${id}/edit`);
    };

    const handleCellClick = (rowIndex, colIndex) => {
        if (crossTpl?.state === DISCARDED || !filledCoordinates?.length) return;
        const wordItem = filledCoordinates.find(
            (item) => item.positions && item.positions.some((p) => p.row === rowIndex && p.col === colIndex)
        );
        if (wordItem) {
            const displayText = wordItem.reversed ? wordItem.text.split("").reverse().join("") : wordItem.text;
            const upper = displayText.toUpperCase();
            setSelectedWord(upper);
            setSearchWord(upper);
            setPositions(wordItem.positions);
        } else {
            setSelectedWord(null);
            setSearchWord("");
            setPositions([]);
        }
    };

    const handleRearrangeWords = async () => {
        if (crossTpl?.state === DISCARDED) {
            handleSnackbar("This puzzle has been discarded and is no longer available.", "warning");
            return;
        }

        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = "wait";

        try {
            setError(null);
            await AdminCrossBoardService.rearrangeWords(crossTpl.id);
            handleSnackbar("Words rearranged successfully", "success");
            setRefresh((prev) => prev + 1);
            setSelectedWord(null);
            setSearchWord("");
            setPositions([]);
        } catch (err) {
            Logger.error("Error rearranging words:", err);
            const errorMessage = err.message || "Failed to rearrange words. Please try again.";
            setError(errorMessage);
            handleSnackbar(errorMessage, "error");
        } finally {
            document.body.style.cursor = originalCursor || "default";
        }
    };


    if (loading) {
        return (
            <Box sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 400,
                p: 3
            }}>
                <Typography variant="h6">Loading puzzle data...</Typography>
            </Box>
        );
    }

    if (fetchError || !crossTpl) {
        return (
            <Box sx={{ mt: 2, p: 3 }}>
                <Alert severity="error">
                    {fetchError || "Puzzle not found"}
                </Alert>
            </Box>
        );
    }

    // Check if puzzle is discarded
    if (crossTpl.state === DISCARDED) {
        return (
            <Box sx={{ mt: 2, p: 3 }}>
                <Alert severity="warning">
                    This puzzle has been discarded and is no longer available.
                </Alert>
            </Box>
        );
    }


    return (
        <Box sx={{ width: "100%" }}>
            {/* Workflow Stepper */}
            <PuzzleWorkflowStepper
                currentState={crossTpl?.state}
                puzzleId={crossTpl?.id}
                size="small"
                align="left"
                onStepClick={() => setRefresh(Date.now())}
                puzzleType={crossTpl?.type}
            />
            
            {/* Header with back button */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ flex: 1 }}>
                    <BorderHeader
                        onBack={handleBack}
                        puzzle={crossTpl}
                        puzzleType="crossword"
                    />
                </Box>
                {crossTpl?.state !== DISCARDED && (
                    <Box sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="Find word candidates">
                            <IconButton
                                onClick={handleFindWordCandidates}
                                size="large"
                                sx={{
                                    color: '#fff',
                                    backgroundColor: '#2e7d32',
                                    '&:hover': {
                                        backgroundColor: '#1b5e20',
                                    },
                                    transition: 'background-color 0.3s',
                                    width: 56,
                                    height: 56
                                }}
                            >
                                <AddIcon sx={{ fontSize: 28 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Rearrange words in the puzzle">
                            <IconButton
                                onClick={handleRearrangeWords}
                                size="large"
                                sx={{
                                    color: '#fff',
                                    backgroundColor: '#1976d2',
                                    '&:hover': {
                                        backgroundColor: '#1565c0',
                                    },
                                    transition: 'background-color 0.3s',
                                    width: 56,
                                    height: 56
                                }}
                            >
                                <SwapHorizIcon sx={{ fontSize: 28 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    width: "100%",
                    alignItems: "stretch",
                    gap: 0,
                    mt: 3
                }}
            >
                {/* Left Panel - Word Search, Word List, Word Meaning (collapsible) */}
                <Box
                    sx={{
                        width: leftPanelFolded ? 0 : 360,
                        minWidth: leftPanelFolded ? 0 : 320,
                        maxWidth: leftPanelFolded ? 0 : 420,
                        overflow: "hidden",
                        transition: "width 0.25s ease-in-out",
                        flexShrink: 0
                    }}
                >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pr: leftPanelFolded ? 0 : 2, width: "100%", minWidth: 0 }}>
                        {/* Word Search */}
                        <Paper elevation={2} sx={{ p: 2 }}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                {/* Hidden Message Alert */}
                                {crossTpl?.hiddenMessage && (
                                    <Alert severity="info" sx={{ mb: 1 }}>
                                        <Typography variant="body2">
                                            Hidden message: <span style={{ fontWeight: "bold" }}>{crossTpl.hiddenMessage.toUpperCase()}</span>
                                        </Typography>
                                    </Alert>
                                )}

                                <TextField
                                    label="Word Search"
                                    value={searchWord}
                                    onChange={handleWordSearch}
                                    helperText="Type the word you want to find in the puzzle"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    disabled={crossTpl.state === DISCARDED}
                                />
                            </Box>
                        </Paper>

                        {/* Word List */}
                        <WordList
                            filledCoordinates={filledCoordinates}
                            selectedWord={selectedWord}
                            onWordSelect={crossTpl.state === DISCARDED ? undefined : (word) => {
                                if (!word) {
                                    Logger.warn("onWordSelect called with null/undefined word");
                                    return;
                                }

                                setSelectedWord(word);
                                const upperWord = word.toUpperCase();
                                setSearchWord(upperWord);
                                if (square.length > 0) {
                                    const wordItem = filledCoordinates.find(item => {
                                        if (!item.text) return false;
                                        const displayText = item.reversed ? item.text.split("").reverse().join("") : item.text;
                                        return displayText.toUpperCase() === upperWord;
                                    });

                                    if (wordItem && wordItem.positions) {
                                        setPositions(wordItem.positions);
                                    } else {
                                        let foundPositions = patternSearch(square, upperWord);
                                        if (foundPositions.length === 0) {
                                            foundPositions = findScatteredWordPositions(filledCoordinates, upperWord);
                                        }
                                        setPositions(foundPositions);
                                    }
                                }
                            }}
                            showVisibilityToggle={crossTpl.state !== DISCARDED}
                            onVisibilityToggle={setShowAllWords}
                            showAllWords={showAllWords}
                            maxHeight={600}
                            onDeleteWord={crossTpl.state === DISCARDED ? undefined : handleDeleteWord}
                        />

                        {/* Word Validation */}
                        {crossTpl.state !== DISCARDED && (
                            <WordValidation filledCoordinates={filledCoordinates} language={crossTpl?.language} isKids={crossTpl?.isKids || false} difficultyLevel={crossTpl?.difficultyLevel} />
                        )}

                        {/* Word Meaning */}
                        {crossTpl.state !== DISCARDED && (
                            <WordMeaning
                                selectedWord={selectedWord}
                                wordData={selectedWordData}
                                onSaveDefinition={handleSaveTitle}
                                gridTplId={crossTpl?.id}
                                language={crossTpl?.language}
                            />
                        )}
                    </Box>
                </Box>

                {/* Right Panel - Puzzle Grid (collapse/expand button at top-left corner inside grid) */}
                <Box sx={{ flex: 1, minWidth: 0, ml: 2, position: "relative" }}>
                    {crossTpl.state === DISCARDED ? (
                        <Box sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: 400,
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                            p: 2,
                            backgroundColor: "#f5f5f5"
                        }}>
                            <Typography variant="h6" color="text.secondary">
                                Puzzle has been discarded
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{
                            display: "flex",
                            justifyContent: "center",
                            overflow: "auto",
                            maxHeight: 700,
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                            p: 2,
                            position: "relative"
                        }}>
                            {/* Collapse/expand left panel - green arrow at top-left corner of grid */}
                            <Tooltip title={leftPanelFolded ? "Expand left panel" : "Fold left panel"}>
                                <IconButton
                                    onClick={() => setLeftPanelFolded(!leftPanelFolded)}
                                    aria-label={leftPanelFolded ? "Expand left panel" : "Fold left panel"}
                                    size="small"
                                    sx={{
                                        position: "absolute",
                                        top: 4,
                                        left: 4,
                                        zIndex: 1,
                                        width: 28,
                                        height: 28,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 0,
                                        backgroundColor: "#4caf50",
                                        color: "white",
                                        "&:hover": { backgroundColor: "#43a047" }
                                    }}
                                >
                                    {leftPanelFolded ? <ChevronRightIcon sx={{ fontSize: 18 }} /> : <ChevronLeftIcon sx={{ fontSize: 18 }} />}
                                </IconButton>
                            </Tooltip>
                            <Box sx={{
                                display: "flex",
                                justifyContent: "center",
                                overflow: "auto",
                                maxHeight: 700
                            }}>
                                <Box>
                                    {square.length > 0 ? (
                                        <>
                                            {/* Column numbers header (0-based) */}
                                            <div style={{ display: "flex", gap: "1px", marginBottom: "1px" }}>
                                                <div style={{
                                                    width: "30px",
                                                    height: "30px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: "12px",
                                                    fontWeight: "bold"
                                                }}>
                                                </div>
                                                {Array.from({ length: square[0]?.length || 12 }, (_, colIndex) => (
                                                    <div
                                                        key={colIndex}
                                                        style={{
                                                            width: "50px",
                                                            height: "30px",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            fontSize: "12px",
                                                            fontWeight: "bold",
                                                            color: "#1976d2"
                                                        }}
                                                    >
                                                        {colIndex}
                                                    </div>
                                                ))}
                                            </div>
                                            {/* Grid rows with row numbers (0-based) */}
                                            {square.map((row, rowIndex) => (
                                                <div key={rowIndex} style={{ display: "flex", gap: "1px", marginBottom: "1px" }}>
                                                    <div style={{
                                                        width: "30px",
                                                        height: "50px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontSize: "12px",
                                                        fontWeight: "bold",
                                                        color: "#1976d2"
                                                    }}>
                                                        {rowIndex}
                                                    </div>
                                                    {showAllWords ? (
                                                        <AllPuzRow
                                                            rowIndex={rowIndex}
                                                            row={row}
                                                            allWordPositions={allWordPositions}
                                                        />
                                                    ) : (
                                                        <PuzRow
                                                            rowIndex={rowIndex}
                                                            row={row}
                                                            selectedWord={searchWord}
                                                            positions={positions}
                                                            onCellClick={handleCellClick}
                                                        />
                                                    )}
                                                </div>
                                            ))}
                                        </>
                                    ) : (
                                        <Typography color="text.secondary" sx={{ p: 2 }}>
                                            {crossTpl?.filledCoordinates ? "Loading puzzle grid..." : "No puzzle grid data available"}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Step Footer with Action Buttons */}
            {crossTpl.state !== DISCARDED && (
                <StepFooter
                    stepType={crossTpl.state === READY_TO_REVIEW ? "ready-for-review" : "draft"}
                    title={crossTpl.state === READY_TO_REVIEW ? "Ready to Review Stage" : "Draft Stage"}
                    description={crossTpl.state === READY_TO_REVIEW
                        ? "Puzzle is ready for review. You can go back to draft or proceed to review stage."
                        : "Review and edit the puzzle content. Once ready, move to review stage."}
                    descriptionExtra={crossTpl.state === DRAFT ? (
                        <Typography variant="body2" component="span" sx={{ mr: 1 }}>
                            {puzzleStats.percentage.toFixed(1)}% complete
                            {puzzleStats.thresholdMet ? (
                                <span style={{ color: "#2e7d32" }}> – {puzzleStats.thresholdPercentage}% threshold met</span>
                            ) : (
                                <span style={{ color: "#d32f2f" }}> – {puzzleStats.thresholdPercentage}% threshold not met ({puzzleStats.cellsNeeded} more {puzzleStats.cellsNeeded === 1 ? "cell" : "cells"} needed)</span>
                            )}
                        </Typography>
                    ) : null}
                    onDiscard={handleDiscardTemplate}
                    onNextStep={handleNextStep}
                    onGoBackDraft={() => {
                        // Handle going back to draft stage
                        AdminCrossBoardService.updateCrossTplState(crossTpl.id, DRAFT).then(() => {
                            setRefresh(prev => prev + 1);
                        }).catch(err => {
                            Logger.error("Error updating puzzle state:", err);
                            setError("Failed to move back to draft stage. Please try again.");
                        });
                    }}
                    onReview={() => {
                        // Navigate to review page
                        navigate(`/admin/puzzle/cross_board/ready-to-review/${crossTpl.id}`);
                    }}
                />
            )}

            {/* Edit Definition Dialog */}
            {crossTpl.state !== DISCARDED && (
                <WordRedefine
                    open={openDialog}
                    onClose={handleDialogCancel}
                    onSave={handleDialogSave}
                    word={editingWord}
                    definition={editingDefinition}
                />
            )}

            <CustomSnackbar
                open={openSnackbar}
                setOpen={setOpenSnackbar}
                message={snackbarMessage}
                severity={snackbarSeverity}
            />

            {/* Confirmation Dialog for Discarding Template */}
            <ConfirmDialog
                open={openConfirmDialog}
                setOpen={setOpenConfirmDialog}
                title="Confirm Discard Template"
                message="Are you sure you want to discard this template? This action cannot be undone."
                targetName={`'${crossTpl?.title || 'Untitled Template'}' will be discarded`}
                targetId={crossTpl?.id}
                callback={handleConfirmDiscard}
            />

            {/* Confirmation Dialog for Deleting Word */}
            <ConfirmDialog
                open={openDeleteWordDialog}
                setOpen={setOpenDeleteWordDialog}
                title="Confirm Delete Word"
                message="Do you want to remove this word from the puzzle?"
                targetName={`'${wordToDelete}' will be removed from the puzzle`}
                targetId={wordToDelete}
                callback={handleConfirmDeleteWord}
            />

            {/* Puzzle Type Selection Dialog */}
            <VerifyDialog
                open={puzzleTypeDialogOpen}
                onClose={() => setPuzzleTypeDialogOpen(false)}
                puzzleId={crossTpl?.id}
                requestPuzzle={AdminCrossBoardService.requestCrossTpl}
                defaultPuzzleType="CROSSWORD"
                allowedPuzzleTypes={["CROSSWORD"]}
                onPuzzleTypeSelected={handlePuzzleTypeSelected}
                onShowSnackbar={handleSnackbar}
                onRemoveWords={async (updatedFilledCoordinates) => {
                    if (!crossTpl?.id) return;
                    await AdminCrossBoardService.updatePuzzleCoordinates(crossTpl.id, updatedFilledCoordinates, null);
                    setRefresh((prev) => prev + 1);
                }}
            />
        </Box>
    );
}

